import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Box, Paper, Typography, useTheme } from "@mui/material";

/**
 * A styled bar chart component with consistent styling
 *
 * @param {Object} props - Component props
 * @param {Array} props.data - The data array for the chart
 * @param {String} props.dataKey - The key to use for the bar values
 * @param {String} props.title - Optional chart title
 * @param {String} props.subtitle - Optional chart subtitle
 * @param {String} props.xAxisDataKey - The key to use for x-axis labels
 * @param {Number} props.height - Chart height in pixels (default: 300)
 * @param {String} props.fill - Bar color (default: theme.palette.primary.main)
 * @param {Boolean} props.isLoading - Loading state
 * @param {Boolean} props.showEmptyState - Whether to show empty state when no data
 * @param {String} props.emptyStateMessage - Message to show in empty state
 */
const StyledBarChart = ({
  data = [],
  dataKey = "value",
  title,
  subtitle,
  xAxisDataKey = "name",
  height = 300,
  fill,
  isLoading = false,
  showEmptyState = true,
  emptyStateMessage = "No data available",
}) => {
  const theme = useTheme();
  const barColor = fill || theme.palette.primary.main;

  const hasData = data && data.length > 0;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        height: "100%",
        borderRadius: 3,
        border: "1px solid rgba(0, 0, 0, 0.05)",
      }}
    >
      {title && (
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            mb: subtitle ? 0.5 : 2,
          }}
        >
          {title}
        </Typography>
      )}

      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {subtitle}
        </Typography>
      )}

      {isLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height={height}
        >
          <Typography variant="body1" color="text.secondary">
            Loading chart data...
          </Typography>
        </Box>
      ) : !hasData && showEmptyState ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height={height}
        >
          <Typography variant="body1" color="text.secondary">
            {emptyStateMessage}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ width: "100%", height }}>
          <ResponsiveContainer>
            <BarChart
              data={data}
              margin={{
                top: 20,
                right: 20,
                left: -24,
                bottom: -10,
              }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke={theme.palette.divider}
              />
              <XAxis
                dataKey={xAxisDataKey}
                tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                tickLine={{ stroke: theme.palette.divider }}
                axisLine={{ stroke: theme.palette.divider }}
              />
              <YAxis
                tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                tickLine={{ stroke: theme.palette.divider }}
                axisLine={{ stroke: theme.palette.divider }}
                tickFormatter={(value) =>
                  Intl.NumberFormat("en-US", {
                    notation: "compact",
                    maximumFractionDigits: 0,
                  }).format(value)
                }
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 8,
                  boxShadow: theme.shadows[2],
                }}
                formatter={(value, name) => [
                  `${Intl.NumberFormat("en-US", {
                    notation: "compact",
                    maximumFractionDigits: 0,
                  }).format(value)}`,
                  name,
                ]}
              />
              <Bar
                dataKey={dataKey}
                fill={barColor}
                barSize={50}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Paper>
  );
};

export default StyledBarChart;
